import React, { useRef, type ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import configJSON from 'config.json';
import { isDevelopment } from 'utils/environment';
import { getTenantById, type TenantId } from '@tenants';
import { AllowedFeatureFlagKey } from 'tenants/types';
import { initAuth, AuthProvider } from '@bedrock/auth';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { mergeAmplifyConfig } from 'services/amplify/configure';
import { trackEvent } from 'observability/utils/event-tracker';

import type { User } from '@bedrock/auth';
import type { SSRFeatureFlagResults } from 'contexts/growthbook/growthbook-ssr-payload';

interface AppAuthProviderProps {
  ssrUser: User | null;
  ssrFeatureFlagResults?: SSRFeatureFlagResults;
  tenantId?: TenantId;
  children: ReactNode;
}

export default function AppAuthProvider({
  ssrUser,
  ssrFeatureFlagResults,
  tenantId,
  children,
}: AppAuthProviderProps) {
  const { t: authT } = useTranslation('formError');
  const tenant = getTenantById(tenantId);
  const useCognitoAuth = ssrFeatureFlagResults?.[AllowedFeatureFlagKey.USE_COGNITO_AUTH] ?? false;
  const cognitoConfig = configJSON.auth?.cognito;

  const authSessionRef = useRef<ReturnType<typeof initAuth> | null>(null);
  if (!authSessionRef.current) {
    authSessionRef.current = initAuth({
      useCognitoAuth,
      themeName: tenant.theme.id,
      translation: { t: authT },
      ssrUser,
      clientAppHost: isDevelopment ? undefined : configJSON.services.clientApp.clientSideHost,
      authApiHost: isDevelopment ? undefined : configJSON.services.authApi.clientSideHost,
      debug: isDevelopment,
      cognitoConfig: cognitoConfig ? {
        userPoolId: cognitoConfig.userPoolId,
        clientId: cognitoConfig.clientId,
        region: cognitoConfig.region,
        domain: cognitoConfig.domain,
        redirectSignIn: typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback`
          : cognitoConfig.redirectSignIn,
        redirectSignOut: typeof window !== 'undefined'
          ? `${window.location.origin}/auth/callback`
          : cognitoConfig.redirectSignOut,
      } : undefined,
      mergeAmplifyConfig,
      trackEvent,
      restoreReturnTo: (target: string) => { window.location.replace(target); },
      secureCookies: !isDevelopment,
    });
  }

  return (
    <GoogleOAuthProvider clientId={tenant.sso.googleWebClientId}>
      <AuthProvider session={authSessionRef.current}>
        {children}
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
